import React, { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Target, Copy, Check, Mail, FileText, Share2, Download } from "lucide-react";
import { marketingContent, emailSignature } from "../mock";
import { useToast } from "../hooks/use-toast";
import { LeadSourceReport } from "./LeadSourceReport";

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through to legacy */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

const CopyBtn = ({ text, label = "Copy" }) => {
  const { toast } = useToast();
  const [done, setDone] = useState(false);
  const copy = async () => {
    const ok = await copyToClipboard(text);
    setDone(ok);
    toast({ title: ok ? "Copied to clipboard" : "Copy failed — please select and copy manually" });
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <Button size="sm" variant="outline" onClick={copy}>
      {done ? <Check className="mr-2 h-4 w-4 text-[#2E9E6A]" /> : <Copy className="mr-2 h-4 w-4" />}
      {label}
    </Button>
  );
};

const MarketingPanel = ({ leads, stats }) => {
  const goal = marketingContent.goal;
  const clients = stats?.clients ?? 0;
  const pct = Math.min(100, Math.round((clients / goal) * 100));

  return (
    <div className="space-y-6">
      <LeadSourceReport leads={leads} />
      {/* Goal tracker */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-[#E9F6EF] text-[#23875A]">
            <Target className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-display font-bold text-[#0E2A33]">Goal: First {goal} Clients</h3>
            <p className="text-sm text-slate-500">Active clients converted so far</p>
          </div>
          <div className="ml-auto font-display font-extrabold text-3xl text-[#0E5A72]">{clients}/{goal}</div>
        </div>
        <div className="h-4 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-[#2E9E6A] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-sm text-slate-500 mt-2">
          {clients >= goal
            ? "🎉 Goal reached! Time to raise the bar."
            : `${goal - clients} more to reach your goal. Keep working your call list and referrals!`}
        </p>
      </div>

      {/* Outreach templates */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Mail className="h-5 w-5 text-[#0E5A72]" />
          <h3 className="font-display font-bold text-[#0E2A33]">Referral Outreach Templates</h3>
          <a href="/logo.svg" download="caregivers-alliance-homecare-logo.svg" className="ml-auto">
            <Button size="sm" variant="outline" className="border-[#0E5A72] text-[#0E5A72]">
              <Download className="mr-2 h-4 w-4" /> Download Logo
            </Button>
          </a>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Each email includes your fixed signature. Attach the downloaded logo to add your branding.
        </p>
        <div className="grid gap-4 md:grid-cols-3">
          {marketingContent.outreach.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-100 p-4 flex flex-col">
              <Badge className="w-fit bg-[#EAF4F7] text-[#0E5A72] border border-[#cfe6ee]">{o.audience}</Badge>
              <div className="text-sm font-semibold text-[#0E2A33] mt-3">{o.subject}</div>
              <Textarea readOnly value={`${o.body}\n\n${emailSignature}`} rows={8} className="mt-2 text-xs bg-[#F7FBFC]" />
              <div className="mt-3 flex gap-2">
                <CopyBtn text={`Subject: ${o.subject}\n\n${o.body}\n\n${emailSignature}`} label="Copy email" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Marketing kit */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-[#0E5A72]" />
            <h3 className="font-display font-bold text-[#0E2A33]">Flyer Text</h3>
          </div>
          <Textarea readOnly value={marketingContent.flyer} rows={12} className="text-xs bg-[#F7FBFC]" />
          <div className="mt-3"><CopyBtn text={marketingContent.flyer} label="Copy flyer" /></div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="h-5 w-5 text-[#0E5A72]" />
            <h3 className="font-display font-bold text-[#0E2A33]">Social Media Posts</h3>
          </div>
          <div className="space-y-3">
            {marketingContent.social.map((p, i) => (
              <div key={i} className="rounded-xl border border-slate-100 p-3">
                <p className="text-sm text-slate-700">{p}</p>
                <div className="mt-2"><CopyBtn text={p} label="Copy post" /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingPanel;
